import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import LoadingState from './common/LoadingState';
import ErrorStateView from './common/ErrorState';

// SKIN-39 — one row per dermatologist enrolled in the investigation. Mirrors the
// backend DermatologistActivityResponse row shape (researcher name in clear,
// opaque uuid, and aggregated counts — no patient data).
export interface DermatologistActivityRow {
    researcherUuid: string;
    researcherName: string;
    enrolledPatientsCount: number;
    visitCount: number;
    submissionCount: number;
    visitsWithoutImagesCount: number;
    lastActiveAt: string | null;
}

interface ActivityPerDermatologistProps {
    loading: boolean;
    error?: Error | null;
    rows: DermatologistActivityRow[];
    onRetry?: () => void;
}

const formatDate = (iso: string | null): React.ReactNode => {
    if (!iso) return <Translate id="hospital.analytics.clinical.activity-per-dermatologist.never" />;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

const ActivityPerDermatologist: React.FC<ActivityPerDermatologistProps> = ({ loading, error, rows, onRetry }) => {
    const headCells = useMemo(() => ([
        { id: 'dermatologist', alignment: 'left', label: <Translate id="hospital.analytics.clinical.activity-per-dermatologist.dermatologist" /> },
        { id: 'enrolled', alignment: 'right', label: <Translate id="hospital.analytics.clinical.activity-per-dermatologist.enrolled" /> },
        { id: 'visits', alignment: 'right', label: <Translate id="hospital.analytics.clinical.activity-per-dermatologist.visits" /> },
        { id: 'submissions', alignment: 'right', label: <Translate id="hospital.analytics.clinical.activity-per-dermatologist.submissions" /> },
        { id: 'withoutImages', alignment: 'right', label: <Translate id="hospital.analytics.clinical.activity-per-dermatologist.without-images" /> },
        { id: 'lastActive', alignment: 'left', label: <Translate id="hospital.analytics.clinical.activity-per-dermatologist.last-active" /> },
    ]), []);

    const tableRows = useMemo(() => rows.map(r => ({
        id: r.researcherUuid,
        dermatologist: r.researcherName,
        enrolled: r.enrolledPatientsCount,
        visits: r.visitCount,
        submissions: r.submissionCount,
        withoutImages: r.visitsWithoutImagesCount,
        lastActive: formatDate(r.lastActiveAt),
    })), [rows]);

    if (loading) return <LoadingState />;
    if (error) return <ErrorStateView error={error} onRetry={onRetry} />;

    // EnhancedTable indexes rows[0] to infer column types, so it must not be
    // rendered with an empty list — show an explicit empty state instead.
    if (!rows.length) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                    <Translate id="hospital.analytics.clinical.activity-per-dermatologist.empty" />
                </Typography>
            </Box>
        );
    }

    return <EnhancedTable noHeader noSelectable disableOrder headCells={headCells} rows={tableRows} />;
};

export default ActivityPerDermatologist;
